import React, { useState, useEffect } from 'react';
import { http } from '../../lib/http';
import { minStartDateToronto, tomorrowToronto, nextServiceDayToronto } from '../../lib/time';
import { lookupTruckersPrice, fetchLivePricing, getAllAddOns, sumAddOnsPrice } from '../../lib/pricing';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Loader2,
  Bell,
  LogOut,
  User,
  Home,
  CreditCard,
  Settings,
  Edit2,
  DollarSign,
  Package,
  Truck
} from "lucide-react";

export default function TruckerDashboard() {
  const formatDateWithoutTimezone = (dateString) => {
    if (!dateString) return 'N/A';
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parts[1];
      const day = parts[2].split(' ')[0];
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString();
    }
    return dateString;
  };

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Renewal state
  const [renewalReminder, setRenewalReminder] = useState(null);
  const [showRenewSameModal, setShowRenewSameModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [renewalFormData, setRenewalFormData] = useState({
    newStartDate: '',
    truckerPlanType: 'Truckers 10 Meals a Week',
    truckerPlanTerm: '5 Veg + 5 Non-Veg'
  });

  // Cancel state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Profile edit state
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    address: { street: '', city: '', postalCode: '', deliveryInstructions: '' }
  });

  // Broadcasts
  const [broadcasts, setBroadcasts] = useState([]);

  // Add-ons state
  const [showAddOnModal, setShowAddOnModal] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [addOnLoading, setAddOnLoading] = useState(false);

  // Activation modal (for expired/cancelled truckers)
  const [showActivationModal, setShowActivationModal] = useState(false);

  // Notification ref
  const notificationRef = React.useRef(null);

  // Active tab
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchLivePricing().catch(console.error);
    loadData();

    // Check for payment return status
    const params = new URLSearchParams(window.location.search);
    const renewalSuccess = params.get('renewal_success');
    const renewalCancel = params.get('renewal_cancel');

    if (renewalSuccess === 'true') {
      window.history.replaceState({}, document.title, window.location.pathname);
      setSuccess('✅ Payment successful! Your plan has been renewed.');
      setTimeout(() => setSuccess(''), 8000);
    }
    if (renewalCancel === 'true') {
      window.history.replaceState({}, document.title, window.location.pathname);
      setError('Payment process was cancelled.');
      setTimeout(() => setError(''), 5000);
    }

    const addonSuccess = params.get('addon_success');
    const addonCancel = params.get('addon_cancel');
    const addonSessionId = params.get('session_id');

    if (addonSuccess === 'true' && addonSessionId) {
      window.history.replaceState({}, document.title, window.location.pathname);
      http.post('/trucker/verify-addon-payment', { sessionId: addonSessionId })
        .then(() => {
          setSuccess('✅ Add-ons purchased successfully!');
          setTimeout(() => setSuccess(''), 8000);
          loadData();
        })
        .catch(() => {
          setError('Add-on payment verification failed. Please contact support.');
          setTimeout(() => setError(''), 8000);
        });
    }
    if (addonCancel === 'true') {
      window.history.replaceState({}, document.title, window.location.pathname);
      setError('Add-on purchase was cancelled.');
      setTimeout(() => setError(''), 5000);
    }
  }, []);

  // Click outside to close notifications
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNotifications]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const profileRes = await http.get('/trucker/me');
      if (profileRes.data?.ok) {
        setProfile(profileRes.data.customer);
        const inactiveStatuses = ['expired', 'ExClient', 'cancelled', 'inactive'];
        if (inactiveStatuses.includes(profileRes.data.customer.subscriptionStatus)) {
          setShowActivationModal(true);
        }
      }

      const notificationsRes = await http.get('/trucker/notifications');
      if (notificationsRes.data?.ok) {
        const notifs = notificationsRes.data.notifications;
        setNotifications(notifs);
        setUnreadCount(notifs.filter(n => n.status === 'unread').length);

        const renewalNotif = notifs.find(n => n.type === 'renewal_reminder' && n.status === 'unread');
        if (renewalNotif) {
          const reminderData = renewalNotif.data || {};
          setRenewalReminder({
            id: renewalNotif._id,
            notificationId: renewalNotif.id,
            message: renewalNotif.message,
            daysUntilRenewal: reminderData.days_until_renewal,
            renewalDate: reminderData.renewal_date || 'N/A',
            amountDue: reminderData.amount_due,
            planKey: reminderData.plan_name || 'Your Plan',
            totalAmount: reminderData.amount_due || '0.00',
            renewalId: reminderData.renewal_id
          });
        }
      }

      const broadcastsRes = await http.get('/trucker/broadcasts');
      if (broadcastsRes.data?.ok) {
        setBroadcasts(broadcastsRes.data.broadcasts || []);
      }

    } catch (e) {
      console.error('Error loading trucker data:', e);
      setError(e?.response?.data?.message || e?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await http.post(`/trucker/notifications/${notificationId}/mark-read`);
      loadData();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    if (!window.confirm('Delete this notification permanently?')) return;
    try {
      await http.delete(`/trucker/notifications/${notificationId}`);
      loadData();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  // ---- Renewal Handlers ----
  const handleRenewalDecision = async (notificationId, decision) => {
    setSelectedNotification(notificationId);
    if (decision === 'renew_same') {
      setShowRenewSameModal(true);
    } else if (decision === 'change_plan') {
      setShowChangePlanModal(true);
    } else if (decision === 'cancel') {
      const reason = prompt('Please tell us why you want to cancel your subscription:');
      if (!reason || !reason.trim()) return;
      if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
      try {
        await http.post('/trucker/update-requests', {
          request_type: 'cancellation',
          requested_changes: { decision: 'cancel', cancellation_reason: reason.trim(), notification_id: notificationId },
          notes: `Trucker requested cancellation: ${reason.trim()}`
        });
        if (notificationId) await http.post(`/trucker/notifications/${notificationId}/mark-read`).catch(() => {});
        alert('⚠️ Cancellation request submitted. Our team will contact you to confirm.');
        loadData();
      } catch (error) {
        setError(error.response?.data?.message || 'Failed to submit cancellation request.');
      }
    }
  };

  const submitRenewSame = async () => {
    if (!renewalFormData.newStartDate) {
      setError('Please select a renewal start date');
      return;
    }
    try {
      const response = await http.post('/trucker/renewal/respond', {
        customer_id: profile?.id,
        response_type: 'renew_same',
        target_date: renewalFormData.newStartDate,
        notes: `Trucker confirmed renewal of same plan starting ${renewalFormData.newStartDate}`
      });
      if (response.data?.success) {
        setShowRenewSameModal(false);
        setError('');
        const notificationData = response.data.notification_data;
        if (notificationData) {
          setPaymentDetails({
            title: '✅ Renewal Confirmed',
            plan: notificationData.plan,
            totalAmount: notificationData.total_amount,
            renewalDate: notificationData.new_renewal_date,
            etransferEmail: notificationData.etransfer_email,
            customerName: notificationData.customer_name
          });
          setShowPaymentModal(true);
          if (selectedNotification) {
            http.post(`/trucker/notifications/${selectedNotification}/mark-read`).catch(() => {});
          }
        } else {
          alert('✅ Renewal confirmed!');
        }
        loadData();
      } else {
        setError(response.data?.message || 'Failed to submit renewal');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to submit renewal.');
    }
  };

  const submitChangePlan = async () => {
    if (!renewalFormData.newStartDate) {
      setError('Please select a plan start date');
      return;
    }
    try {
      const response = await http.post('/trucker/renewal/change-plan-checkout', {
        renewal_id: renewalReminder?.renewalId,
        new_plan_details: {
          new_meal_plan_type: renewalFormData.truckerPlanType,
          new_meal_plan_term: renewalFormData.truckerPlanTerm,
          new_billing_term: 'Weekly',
          new_add_ons: []
        }
      });
      if (response.data?.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError('Failed to initiate checkout session.');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to submit plan change.');
    }
  };

  const handlePayNow = async () => {
    if (!renewalReminder?.renewalId) {
      alert('Unable to process payment: Missing renewal details. Please contact support.');
      return;
    }
    try {
      setLoading(true);
      const response = await http.post('/trucker/renewal/checkout-session', {
        renewal_id: renewalReminder.renewalId
      });
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        alert('Error: No checkout URL returned from server.');
        setLoading(false);
      }
    } catch (error) {
      alert('Failed to initiate payment: ' + (error.response?.data?.message || error.message));
      setLoading(false);
    }
  };

  // ---- Profile Handlers ----
  const handleEditProfileClick = () => {
    if (!profile) return;
    setProfileFormData({
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      phone: profile.phone || '',
      address: {
        street: profile.address?.street || '',
        city: profile.address?.city || '',
        postalCode: profile.address?.postalCode || '',
        deliveryInstructions: profile.address?.deliveryInstructions || ''
      }
    });
    setShowEditProfileModal(true);
  };

  const handleUpdateProfile = async () => {
    try {
      if (!profileFormData.firstName || !profileFormData.lastName || !profileFormData.phone) {
        alert('Please fill in Name and Phone fields.');
        return;
      }
      await http.put('/trucker/profile', profileFormData);
      setShowEditProfileModal(false);
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update profile');
    }
  };

  // ---- Cancel Handler ----
  const handleCancelSubscription = async () => {
    try {
      await http.post('/trucker/update-requests', {
        request_type: 'cancellation',
        requested_changes: { decision: 'cancel', cancellation_reason: cancelReason.trim() },
        notes: `Trucker requested cancellation: ${cancelReason.trim()}`
      });
      setShowCancelModal(false);
      setSuccess('⚠️ Cancellation request submitted. Our team will contact you to confirm.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to submit cancellation request.');
    }
  };

  // ---- Add-On Handler ----
  const handleAddOns = async () => {
    if (selectedAddOns.length === 0) return;
    try {
      setAddOnLoading(true);
      const response = await http.post('/trucker/add-addons', { addOns: selectedAddOns });
      if (response.data?.ok && response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError('Failed to create add-on checkout session.');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to add add-ons.');
    } finally {
      setAddOnLoading(false);
    }
  };

  // Trucker plan term options based on type
  const truckerPlanTerms = {
    'Truckers 10 Meals a Week': ['5 Veg + 5 Non-Veg', '10 Veg Meals', '10 Non-Veg Meals'],
    'Truckers 12 Meals a Week': ['6 Veg + 6 Non-Veg', '12 Veg Meals']
  };

  // ---- LOADING ----
  if (loading && !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-orange-500" />
          <p className="text-white">Loading your Trucker dashboard...</p>
        </div>
      </div>
    );
  }

  // ---- RENDER ----
  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-0 relative">

      {/* ===== MODALS ===== */}

      {/* Edit Profile Modal */}
      {showEditProfileModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
          <Card className="w-full max-w-lg bg-white max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Edit Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>First Name</Label><Input value={profileFormData.firstName} onChange={(e) => setProfileFormData({ ...profileFormData, firstName: e.target.value })} /></div>
                <div><Label>Last Name</Label><Input value={profileFormData.lastName} onChange={(e) => setProfileFormData({ ...profileFormData, lastName: e.target.value })} /></div>
              </div>
              <div><Label>Phone</Label><Input value={profileFormData.phone} onChange={(e) => setProfileFormData({ ...profileFormData, phone: e.target.value })} /></div>
              <div><Label>Street</Label><Input value={profileFormData.address.street} onChange={(e) => setProfileFormData({ ...profileFormData, address: { ...profileFormData.address, street: e.target.value } })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>City</Label><Input value={profileFormData.address.city} onChange={(e) => setProfileFormData({ ...profileFormData, address: { ...profileFormData.address, city: e.target.value } })} /></div>
                <div><Label>Postal Code</Label><Input value={profileFormData.address.postalCode} onChange={(e) => setProfileFormData({ ...profileFormData, address: { ...profileFormData.address, postalCode: e.target.value } })} /></div>
              </div>
              <div><Label>Delivery Instructions</Label><textarea className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={profileFormData.address.deliveryInstructions} onChange={(e) => setProfileFormData({ ...profileFormData, address: { ...profileFormData.address, deliveryInstructions: e.target.value } })} /></div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowEditProfileModal(false)}>Cancel</Button>
                <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={handleUpdateProfile}>Save Changes</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
          <Card className="w-full max-w-md bg-white">
            <CardHeader><CardTitle className="text-red-600">Cancel Subscription</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">Are you sure you want to cancel? Please tell us why:</p>
              <textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Reason for cancellation..." value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
              <div className="flex gap-3">
                <Button onClick={handleCancelSubscription} className="flex-1 bg-red-600 hover:bg-red-700 text-white" disabled={!cancelReason.trim()}>Confirm Cancellation</Button>
                <Button onClick={() => setShowCancelModal(false)} variant="outline" className="flex-1">Keep My Plan</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add-On Modal */}
      {showAddOnModal && (() => {
        const allAddOns = getAllAddOns();
        const currentAddOns = profile?.addOns || [];
        const availableAddOns = Object.keys(allAddOns).filter(a => !currentAddOns.includes(a));
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
            <Card className="w-full max-w-lg bg-white max-h-[90vh] overflow-y-auto">
              <CardHeader className="bg-gradient-to-r from-black to-gray-900 rounded-t-lg">
                <CardTitle className="text-white flex items-center gap-2"><Package className="w-5 h-5 text-orange-500" /> Add Add-Ons</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {currentAddOns.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-500 mb-2">Current Add-Ons</p>
                    <div className="flex flex-wrap gap-2">
                      {currentAddOns.map(a => <span key={a} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">{a}</span>)}
                    </div>
                  </div>
                )}
                {availableAddOns.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">You already have all available add-ons!</p>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-gray-700">Select add-ons to purchase:</p>
                    {availableAddOns.map(addon => (
                      <label key={addon} className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-colors ${selectedAddOns.includes(addon) ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}`}>
                        <div className="flex items-center gap-3">
                          <input type="checkbox" checked={selectedAddOns.includes(addon)} onChange={(e) => setSelectedAddOns(prev => e.target.checked ? [...prev, addon] : prev.filter(a => a !== addon))} className="w-4 h-4 accent-orange-500" />
                          <span className="font-medium text-gray-900">{addon}</span>
                        </div>
                        <span className="font-bold text-orange-600">${allAddOns[addon]?.Weekly || 0}/wk</span>
                      </label>
                    ))}
                    {selectedAddOns.length > 0 && (
                      <div className="bg-orange-50 border-2 border-orange-200 p-3 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Additional Weekly Cost</p>
                        <p className="text-xl font-bold text-orange-600">${sumAddOnsPrice(selectedAddOns, 'Weekly')}/wk</p>
                        <p className="text-xs text-gray-500 mt-1">Prorated for remaining days in current cycle</p>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  {availableAddOns.length > 0 && (
                    <Button onClick={handleAddOns} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" disabled={selectedAddOns.length === 0 || addOnLoading}>
                      {addOnLoading ? 'Processing...' : 'Purchase Add-Ons'}
                    </Button>
                  )}
                  <Button onClick={() => { setShowAddOnModal(false); setSelectedAddOns([]); }} variant="outline" className="flex-1">Close</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Payment Details Modal */}
      {showPaymentModal && paymentDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
          <Card className="w-full max-w-md bg-white">
            <CardHeader><CardTitle>{paymentDetails.title}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p><strong>Plan:</strong> {paymentDetails.plan}</p>
                <p><strong>Amount:</strong> ${paymentDetails.totalAmount} (HST-inclusive)</p>
                <p><strong>New Start Date:</strong> {paymentDetails.renewalDate}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="font-semibold mb-2">Payment via E-Transfer:</p>
                <p className="text-lg font-bold text-blue-700">{paymentDetails.etransferEmail}</p>
                <p className="text-sm text-gray-600 mt-2">Send the payment via Interac e-Transfer to the email above. Please include your name in the message.</p>
              </div>
              <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white" onClick={() => setShowPaymentModal(false)}>Got It!</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Renew Same Plan Modal */}
      {showRenewSameModal && (() => {
        const currentNotification = notifications.find(n => n.id === selectedNotification);
        const renewalTotal = currentNotification?.data?.renewal_total || profile?.totalAmount || 0;
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h2 className="text-2xl font-bold mb-4">🔄 Renew Current Plan</h2>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mb-4">
                <p className="text-sm text-black"><strong>Current Plan:</strong> {profile?.planKey || 'N/A'}</p>
                <p className="text-sm text-black mt-1"><strong>Renewal Total:</strong> ${typeof renewalTotal === 'number' ? renewalTotal.toFixed(2) : renewalTotal} (HST-inclusive)</p>
              </div>
              <div className="mb-4">
                <Label htmlFor="newStartDate">Select New Start Date *</Label>
                <Input
                  id="newStartDate"
                  type="date"
                  min={minStartDateToronto()}
                  value={renewalFormData.newStartDate}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    if (selectedDate) {
                      const day = new Date(selectedDate + 'T12:00:00Z').getUTCDay();
                      if (day === 0 || day === 6) {
                        setError('Please select a weekday (Monday-Friday).');
                        setRenewalFormData({ ...renewalFormData, newStartDate: nextServiceDayToronto() });
                        return;
                      }
                      if (selectedDate < tomorrowToronto()) {
                        setError('Start date must be tomorrow or later.');
                        return;
                      }
                      setError('');
                    }
                    setRenewalFormData({ ...renewalFormData, newStartDate: selectedDate });
                  }}
                  className="mt-1"
                />
                <p className="text-xs text-gray-700 mt-1">Choose when your renewed plan should start (weekdays only)</p>
              </div>
              {error && <div className="mb-4 bg-orange-50 border border-orange-200 text-black px-4 py-3 rounded">{error}</div>}
              <div className="flex gap-3">
                <Button onClick={submitRenewSame} className="flex-1 bg-orange-500 hover:bg-orange-600">✅ Confirm Renewal</Button>
                <Button onClick={() => { setShowRenewSameModal(false); setError(''); }} variant="outline" className="flex-1">Cancel</Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Change Plan Modal — Trucker Plans Only */}
      {showChangePlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">🔄 Change Your Trucker Plan</h2>
            <div className="bg-orange-50 p-4 rounded-lg border border-black mb-4">
              <p className="text-sm text-orange-500">Our team will review your request and confirm the exact pricing with you before processing.</p>
            </div>
            <div className="space-y-4">
              <div>
                <Label>New Plan Start Date *</Label>
                <Input
                  type="date"
                  min={minStartDateToronto()}
                  value={renewalFormData.newStartDate}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    if (selectedDate) {
                      const day = new Date(selectedDate + 'T12:00:00Z').getUTCDay();
                      if (day === 0 || day === 6) {
                        setError('Please select a weekday (Monday-Friday).');
                        setRenewalFormData({ ...renewalFormData, newStartDate: nextServiceDayToronto() });
                        return;
                      }
                      setError('');
                    }
                    setRenewalFormData({ ...renewalFormData, newStartDate: selectedDate });
                  }}
                  className="mt-1"
                />
              </div>

              {/* Trucker Plan Type */}
              <div>
                <Label>Trucker Plan Type *</Label>
                <select
                  value={renewalFormData.truckerPlanType}
                  onChange={(e) => {
                    const newType = e.target.value;
                    const terms = truckerPlanTerms[newType] || [];
                    setRenewalFormData({
                      ...renewalFormData,
                      truckerPlanType: newType,
                      truckerPlanTerm: terms[0] || ''
                    });
                  }}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="Truckers 10 Meals a Week">Truckers 10 Meals a Week</option>
                  <option value="Truckers 12 Meals a Week">Truckers 12 Meals a Week</option>
                </select>
              </div>

              {/* Trucker Plan Term */}
              <div>
                <Label>Plan Term *</Label>
                <select
                  value={renewalFormData.truckerPlanTerm}
                  onChange={(e) => setRenewalFormData({ ...renewalFormData, truckerPlanTerm: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                >
                  {(truckerPlanTerms[renewalFormData.truckerPlanType] || []).map(term => (
                    <option key={term} value={term}>{term}</option>
                  ))}
                </select>
              </div>

              {/* Billing Term — locked to Weekly */}
              <div>
                <Label>Billing Term</Label>
                <Input value="Weekly" disabled className="mt-1 bg-gray-100" />
                <p className="text-xs text-gray-500 mt-1">Trucker plans are billed weekly</p>
              </div>
            </div>

            {/* Price Preview */}
            {renewalFormData.truckerPlanTerm && (
              <div className="mt-4 rounded-lg border-2 border-orange-200 bg-orange-50 p-4">
                <h3 className="font-semibold mb-3 text-black text-lg">Price Preview (HST-Inclusive)</h3>
                <div className="flex justify-between text-lg font-bold">
                  <span>Weekly Total:</span>
                  <span className="text-orange-500">
                    ${lookupTruckersPrice(renewalFormData.truckerPlanType, renewalFormData.truckerPlanTerm)}
                  </span>
                </div>
              </div>
            )}

            {error && <div className="mt-4 bg-orange-50 border border-orange-200 text-black px-4 py-3 rounded">{error}</div>}

            <div className="flex gap-3 mt-6">
              <Button onClick={submitChangePlan} className="flex-1 bg-orange-500 hover:bg-orange-600">📤 Submit Plan Change</Button>
              <Button onClick={() => { setShowChangePlanModal(false); setError(''); }} variant="outline" className="flex-1">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Activation Modal (expired/cancelled reboarding) */}
      {showActivationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
          <Card className="w-full max-w-md bg-white">
            <CardHeader><CardTitle className="text-orange-600">Reactivate Subscription</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">Your subscription is currently inactive. Would you like to reactivate?</p>
              <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white" onClick={() => { setShowActivationModal(false); setShowChangePlanModal(true); }}>Reactivate — Choose a Plan</Button>
              <Button variant="outline" className="w-full" onClick={() => setShowActivationModal(false)}>Dismiss</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== HEADER ===== */}
      <div className="bg-black text-white py-3 sm:py-4 px-4 sm:px-6 shadow-xl">
        <div className="container mx-auto sm:flex sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 sm:gap-4 mb-3 sm:mb-0">
            <div className="relative">
              <div className="absolute inset-0 bg-[#FF6A00] rounded-full blur-lg opacity-40"></div>
              <div className="relative bg-white rounded-full p-1 border-2 border-[#FF6A00] shadow-[0_0_15px_rgba(255,106,0,0.4)]">
                <img src="/images/just-protein-logo.png" alt="Just Protein Logo" className="h-10 w-10 sm:h-12 sm:w-12 object-contain" />
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2">
                <Truck className="w-5 h-5 text-orange-500" />
                Just Protein <span className="text-orange-500">Truckers</span>
              </h1>
              <p className="text-xs sm:text-sm text-gray-400">Trucker Dashboard</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <div ref={notificationRef} className="relative">
              <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 rounded-full hover:bg-gray-800 transition-colors">
                <Bell className="w-5 h-5 text-gray-300" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#FF6A00] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{unreadCount}</span>
                )}
              </button>
            </div>

            <button onClick={handleEditProfileClick} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors text-sm">
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">{profile?.firstName || 'Trucker'}</span>
            </button>

            <button onClick={handleLogout} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-800 hover:bg-red-900 transition-colors text-sm text-gray-300 hover:text-white">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div ref={notificationRef} className="fixed right-4 top-16 w-80 sm:w-96 max-h-96 overflow-y-auto bg-white rounded-lg shadow-2xl border-2 border-orange-500 z-50">
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-black to-gray-900 rounded-t-lg">
            <h3 className="text-lg font-bold text-white">Notifications</h3>
          </div>
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No notifications</div>
          ) : (
            <div className="divide-y">
              {notifications.slice(0, 10).map(notif => (
                <div key={notif.id} className={`p-4 ${notif.status === 'unread' ? 'bg-orange-50' : 'bg-white'}`}>
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-semibold text-sm text-gray-900">{notif.title}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded ${notif.status === 'unread' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`}>{notif.status}</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2 whitespace-pre-line">{notif.message}</p>
                  <p className="text-xs text-gray-400">{new Date(notif.created_at).toLocaleString()}</p>

                  {/* Renewal actions */}
                  {notif.type === 'renewal_reminder' && notif.data?.actions && notif.status === 'unread' && (
                    <div className="mt-3 space-y-2">
                      {notif.data.actions.map((action) => (
                        <button
                          key={action.type}
                          onClick={() => handleRenewalDecision(notif.id, action.type)}
                          className={`w-full px-3 py-2 text-sm rounded font-medium transition-colors ${
                            action.type === 'renew_same' ? 'bg-orange-500 hover:bg-orange-600 text-white' :
                            action.type === 'change_plan' ? 'bg-black hover:bg-gray-900 text-white' :
                            'bg-orange-500 hover:bg-orange-600 text-white'
                          }`}
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2 mt-2">
                    {notif.status === 'unread' && notif.type !== 'renewal_reminder' && (
                      <Button size="sm" variant="outline" onClick={() => markNotificationRead(notif.id)}>Mark as Read</Button>
                    )}
                    <Button size="sm" variant="destructive" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => deleteNotification(notif.id)}>Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== MAIN CONTENT ===== */}
      <div className="container mx-auto px-4 sm:px-6 py-6">

        {/* Alerts */}
        {error && <div className="mb-4 bg-orange-50 border-2 border-black text-black px-4 py-3 rounded">⚠️ {error}</div>}
        {success && <div className="mb-4 bg-orange-50 border-2 border-black text-black px-4 py-3 rounded">{success}</div>}

        {/* Broadcasts */}
        {broadcasts.length > 0 && (
          <div className="mb-6 space-y-3">
            {broadcasts.map(broadcast => (
              <div key={broadcast.id} className="bg-orange-50 border-l-4 border-l-black px-6 py-4 rounded-lg shadow-sm">
                <span className="text-black font-semibold">📢 Announcement</span>
                <p className="text-gray-800 mt-1">{broadcast.message}</p>
              </div>
            ))}
          </div>
        )}

        {/* Welcome + Payment Status */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Hello, {profile?.firstName || 'Trucker'}!</h2>
            <p className="text-gray-600 mt-1 text-sm">Welcome to your Trucker dashboard — fuel up and roll on</p>
          </div>
          {profile?.paymentStatus && (
            <div className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap ${
              profile.paymentStatus === 'Paid' ? 'bg-orange-100 text-black border-2 border-black' : 'bg-white text-orange-500 border-2 border-orange-500'
            }`}>
              {profile.paymentStatus === 'Paid' ? '✅ Payment: Paid' : '⏳ Payment: Pending'}
            </div>
          )}
        </div>

        {/* Renewal Reminder Banner */}
        {renewalReminder && (
          <Card className={`mb-6 border-l-4 shadow-md ${
            renewalReminder.daysUntilRenewal <= 0 ? 'bg-red-50 border-l-red-600' :
            renewalReminder.daysUntilRenewal <= 1 ? 'bg-orange-50 border-l-orange-500' : 'bg-blue-50 border-l-blue-500'
          }`}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-xl flex items-center gap-2 ${
                renewalReminder.daysUntilRenewal <= 0 ? 'text-red-800' :
                renewalReminder.daysUntilRenewal <= 1 ? 'text-orange-800' : 'text-blue-800'
              }`}>
                {renewalReminder.daysUntilRenewal <= 0 ? <span className="animate-pulse">⚠️ Payment Overdue</span> :
                  renewalReminder.daysUntilRenewal === 1 ? '⏰ Renewal Due Tomorrow' : 'ℹ️ Upcoming Renewal'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-800 font-medium mb-3">{renewalReminder.message}</p>
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
                <div><p className="text-sm text-gray-600">Plan</p><p className="font-bold text-gray-900">{renewalReminder.planKey}</p></div>
                <div><p className="text-sm text-gray-600">Renewal Date</p><p className="font-bold text-gray-900">{renewalReminder.renewalDate}</p></div>
                <div><p className="text-sm text-gray-600">Amount Due</p><p className="font-bold text-gray-900 text-lg">${renewalReminder.totalAmount}</p></div>
                <div className="flex flex-col gap-2 w-full md:w-auto min-w-[200px]">
                  <Button onClick={handlePayNow} className="w-full bg-green-600 hover:bg-green-700 text-white font-bold">Pay Now (Renew Same)</Button>
                  <Button onClick={() => handleRenewalDecision(renewalReminder.notificationId, 'change_plan')} className="w-full bg-blue-600 hover:bg-blue-700 text-white">Change Plan</Button>
                  <Button onClick={() => handleRenewalDecision(renewalReminder.notificationId, 'cancel')} variant="outline" className="w-full border-red-500 text-red-500 hover:bg-red-50">Cancel Subscription</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab Navigation */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button onClick={() => setActiveTab('overview')} className={`relative flex flex-col items-center justify-center p-4 rounded-lg font-semibold transition-all ${activeTab === 'overview' ? 'bg-orange-500 text-white shadow-lg' : 'bg-white text-gray-600 hover:bg-orange-50 border border-gray-200'}`}>
            <Home className="w-5 h-5 mb-1" /> Overview
          </button>
          <button onClick={() => setActiveTab('myplan')} className={`relative flex flex-col items-center justify-center p-4 rounded-lg font-semibold transition-all ${activeTab === 'myplan' ? 'bg-orange-500 text-white shadow-lg' : 'bg-white text-gray-600 hover:bg-orange-50 border border-gray-200'}`}>
            <Package className="w-5 h-5 mb-1" /> My Plan
          </button>
        </div>

        {/* ===== OVERVIEW TAB ===== */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Plan Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Plan Info */}
              <Card className="bg-white border-2 border-orange-200">
                <CardHeader className="p-4 pb-3 bg-gradient-to-r from-black to-gray-900">
                  <CardTitle className="text-base flex items-center gap-2 text-white">
                    <Truck className="w-4 h-4 text-orange-500" /> Plan Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <div><p className="text-xs text-gray-500">Plan</p><p className="font-bold text-gray-900 text-sm">{profile?.planKey || 'N/A'}</p></div>
                    <div><p className="text-xs text-gray-500">Billing</p><p className="font-semibold text-gray-700 text-sm">Weekly</p></div>
                    <div><p className="text-xs text-gray-500">Status</p>
                      <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                        profile?.plan?.status === 'active' ? 'bg-green-100 text-green-700' :
                        profile?.plan?.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{profile?.plan?.status || 'N/A'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Dates */}
              <Card className="bg-white border-2 border-orange-200">
                <CardHeader className="p-4 pb-3 bg-gradient-to-r from-black to-gray-900">
                  <CardTitle className="text-base flex items-center gap-2 text-white">
                    <CreditCard className="w-4 h-4 text-orange-500" /> Dates
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-2">
                  <div><p className="text-xs text-gray-500">Start Date</p><p className="font-semibold text-sm">{formatDateWithoutTimezone(profile?.planStartDate)}</p></div>
                  <div><p className="text-xs text-gray-500">End Date</p><p className="font-semibold text-sm">{formatDateWithoutTimezone(profile?.planEndDate)}</p></div>
                  <div><p className="text-xs text-gray-500">Renewal Date</p><p className="font-semibold text-orange-600 text-sm">{formatDateWithoutTimezone(profile?.planRenewalDate)}</p></div>
                </CardContent>
              </Card>

              {/* Total Amount */}
              <Card className="bg-white border-2 border-orange-200">
                <CardHeader className="p-4 pb-3 bg-gradient-to-r from-black to-gray-900">
                  <CardTitle className="text-base flex items-center gap-2 text-white">
                    <DollarSign className="w-4 h-4 text-orange-500" /> Amount
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-4 rounded-lg text-white text-center">
                    <p className="text-3xl font-bold mb-1">${profile?.totalAmount || '0'}</p>
                    <p className="text-orange-100 text-xs font-semibold">Weekly (HST Inc.)</p>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Status */}
              <Card className="bg-white border-2 border-orange-200">
                <CardHeader className="p-4 pb-3 bg-gradient-to-r from-black to-gray-900">
                  <CardTitle className="text-base flex items-center gap-2 text-white">
                    <DollarSign className="w-4 h-4 text-orange-500" /> Payment
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className={`p-4 rounded-lg text-center border-2 ${
                    profile?.paymentStatus === 'Paid' ? 'bg-orange-50 border-orange-500' : 'bg-gray-50 border-gray-400'
                  }`}>
                    <p className={`text-2xl font-bold ${profile?.paymentStatus === 'Paid' ? 'text-orange-600' : 'text-gray-600'}`}>
                      {profile?.paymentStatus || 'Pending'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card className="bg-white border-2 border-orange-200 cursor-pointer hover:shadow-md transition-shadow" onClick={handleEditProfileClick}>
                <CardHeader className="p-4 pb-3 bg-gradient-to-r from-black to-gray-900">
                  <CardTitle className="text-base flex items-center gap-2 text-white"><Edit2 className="w-4 h-4 text-orange-500" /> Profile</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white" size="sm">Edit Profile</Button>
                </CardContent>
              </Card>

              <Card className="bg-white border-2 border-orange-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowAddOnModal(true)}>
                <CardHeader className="p-4 pb-3 bg-gradient-to-r from-black to-gray-900">
                  <CardTitle className="text-base flex items-center gap-2 text-white"><Package className="w-4 h-4 text-orange-500" /> Add-Ons</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white" size="sm">
                    {profile?.addOns?.length > 0 ? 'Manage Add-Ons' : 'Browse Add-Ons'}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Active Add-Ons */}
            {profile?.addOns && profile.addOns.length > 0 && (
              <Card className="bg-white border-2 border-orange-200">
                <CardHeader className="p-4 pb-3 bg-gradient-to-r from-black to-gray-900">
                  <CardTitle className="text-base flex items-center gap-2 text-white">
                    <Package className="w-4 h-4 text-orange-500" /> Active Add-Ons
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {profile.addOns.map((addon, idx) => (
                      <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">{addon}</span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Delivery Address */}
            <Card className="bg-white border-2 border-orange-200">
              <CardHeader className="p-4 pb-3 bg-gradient-to-r from-black to-gray-900">
                <CardTitle className="text-base flex items-center gap-2 text-white">
                  <Truck className="w-4 h-4 text-orange-500" /> Delivery Address
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <p className="text-gray-700">{profile?.address?.street || 'N/A'}, {profile?.address?.city || ''} {profile?.address?.postalCode || ''}</p>
                {profile?.address?.deliveryInstructions && (
                  <p className="text-sm text-gray-500 mt-1"><strong>Instructions:</strong> {profile.address.deliveryInstructions}</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== MY PLAN TAB ===== */}
        {activeTab === 'myplan' && (
          <div className="space-y-6">
            <Card className="bg-white border-2 border-orange-200">
              <CardHeader className="p-4 bg-gradient-to-r from-black to-gray-900">
                <CardTitle className="text-lg text-white flex items-center gap-2"><Package className="w-5 h-5 text-orange-500" /> My Trucker Plan</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-sm text-gray-500">Plan Type</p><p className="font-bold">{profile?.plan?.name || 'N/A'}</p></div>
                  <div><p className="text-sm text-gray-500">Plan Term</p><p className="font-bold">{profile?.plan?.term || 'N/A'}</p></div>
                  <div><p className="text-sm text-gray-500">Billing</p><p className="font-bold">Weekly</p></div>
                  <div><p className="text-sm text-gray-500">Status</p><p className="font-bold capitalize">{profile?.plan?.status || 'N/A'}</p></div>
                  <div><p className="text-sm text-gray-500">Start Date</p><p className="font-bold">{formatDateWithoutTimezone(profile?.planStartDate)}</p></div>
                  <div><p className="text-sm text-gray-500">Renewal Date</p><p className="font-bold text-orange-600">{formatDateWithoutTimezone(profile?.planRenewalDate)}</p></div>
                </div>
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 rounded-lg text-white text-center">
                  <p className="text-xs mb-1">Total Weekly Amount (HST Inc.)</p>
                  <p className="text-3xl font-bold">${profile?.totalAmount || '0'}</p>
                </div>

                {/* Current add-ons in plan view */}
                {profile?.addOns && profile.addOns.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-500 mb-2">Active Add-Ons</p>
                    <div className="flex flex-wrap gap-2">
                      {profile.addOns.map((addon, idx) => (
                        <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">{addon}</span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add-Ons */}
            <Card className="bg-white border-2 border-orange-200">
              <CardHeader className="p-4 bg-gradient-to-r from-black to-gray-900">
                <CardTitle className="text-base flex items-center gap-2 text-white"><Package className="w-4 h-4 text-orange-500" /> Add-Ons</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <Button onClick={() => setShowAddOnModal(true)} className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                  {profile?.addOns?.length > 0 ? 'Add More Add-Ons' : 'Browse & Purchase Add-Ons'}
                </Button>
              </CardContent>
            </Card>

            {/* Manage Subscription - NO PAUSE */}
            <Card className="bg-white border-2 border-orange-200">
              <CardHeader className="p-4 bg-gradient-to-r from-black to-gray-900">
                <CardTitle className="text-base flex items-center gap-2 text-white"><Settings className="w-4 h-4 text-orange-500" /> Manage Subscription</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <Button onClick={() => setShowChangePlanModal(true)} className="w-full bg-blue-600 hover:bg-blue-700 text-white">Change Plan</Button>
                <Button onClick={() => setShowCancelModal(true)} variant="outline" className="w-full border-red-500 text-red-500 hover:bg-red-50">Cancel Subscription</Button>
              </CardContent>
            </Card>
          </div>
        )}

      </div>

      {/* Mobile Bottom Nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-black border-t-2 border-orange-500 md:hidden z-40">
        <div className="flex justify-around py-2">
          <button onClick={() => setActiveTab('overview')} className={`flex flex-col items-center p-1 ${activeTab === 'overview' ? 'text-orange-500' : 'text-gray-400'}`}>
            <Home className="w-5 h-5" /><span className="text-xs mt-1">Home</span>
          </button>
          <button onClick={() => setActiveTab('myplan')} className={`flex flex-col items-center p-1 ${activeTab === 'myplan' ? 'text-orange-500' : 'text-gray-400'}`}>
            <Package className="w-5 h-5" /><span className="text-xs mt-1">Plan</span>
          </button>
          <button onClick={() => setShowNotifications(true)} className={`flex flex-col items-center p-1 relative text-gray-400`}>
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && <span className="absolute -top-1 right-0 bg-[#FF6A00] text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{unreadCount}</span>}
            <span className="text-xs mt-1">Alerts</span>
          </button>
        </div>
      </div>
    </div>
  );
}
